import { useEffect, useRef, useState, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { IconDownload } from '../icons/index.js';

/**
 * LE VOILE DE DEPOT : ce qui apparait quand on GLISSE un fichier au-dessus de
 * la fenetre (recette du 02/11). Le voile floute des fenetres du socle (celui
 * du `Modal`), et au milieu une petite fenetre au cadre en pointilles : la
 * fleche de depot, la destination (« Déposer dans Photos ») et ce qui passe
 * (« Images · 10 Mo max »). Rien d'autre : ni croix, ni bouton. Il n'existe
 * que pendant le glisser, et se ferme seul quand on lache le fichier ou qu'on
 * sort de la fenetre.
 *
 * IL NE PREND AUCUN CLIC NI AUCUN GLISSER (`pointer-events-none`) : le depot
 * vaut sur toute la fenetre, pas seulement dans le cadre, et c'est
 * `useGlisserDeFichiers` qui l'ecoute sur `window`. Rendu dans un portail sur
 * le body, comme la visionneuse : sous le `contain: layout` d'une vue, un
 * `fixed` se poserait sur la vue, pas sur l'ecran.
 */
export function VoileDeDepot({ ouvert, titre, precision }: {
  ouvert: boolean;
  /** La destination, au nom de la section : « Déposer dans Photos ». */
  titre: string;
  /** Ce qui passe, en une ligne : « Images · 10 Mo max ». */
  precision?: string | undefined;
}): JSX.Element | null {
  if (!ouvert) return null;
  return createPortal(
    <div aria-hidden className="pointer-events-none fixed inset-0 z-30 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-scrim backdrop-blur-sm taki-anim-fade" />
      <div className="relative w-[20rem] max-w-full rounded-2xl border border-border bg-surface p-2 shadow-panel taki-anim-pop">
        <div className="flex flex-col items-center gap-1 rounded-xl border-2 border-dashed border-border-strong px-6 py-8 text-center">
          <IconDownload size={32} className="mb-2 text-ink-700" />
          <p className="text-sm font-medium text-ink-900">{titre}</p>
          {precision !== undefined && precision !== '' && <p className="text-xs text-ink-500">{precision}</p>}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/*
 * UN GESTE DE DEPOT N'ENVOIE QU'UNE FOIS (recette du 02/11 : chaque photo
 * deposee arrivait deux fois). Trois gardes, parce que la cause n'a pas ete
 * reproduite hors du poste du fondateur :
 *  - l'EVENEMENT est marque des qu'un ecouteur le prend : deux ecouteurs
 *    (deux cartes montees) ne l'envoient pas deux fois. La marque est un
 *    `Symbol.for`, commun a toutes les copies du module ;
 *  - la LISTE est dedoublonnee (`fichiersUniques`) ;
 *  - le MEME DEPOT repete dans la seconde (deux evenements pour un geste) est
 *    le meme geste (`memeGeste`).
 */
const MARQUE_DE_DEPOT = Symbol.for('taki.depot-traite');
const DERNIER_DEPOT = Symbol.for('taki.dernier-depot');
/** Deux depots des memes fichiers a moins d'une seconde d'intervalle sont un seul geste. */
export const DELAI_D_UN_GESTE_MS = 1000;

/** La clef d'un fichier : meme nom, meme taille, meme date et meme type, c'est le meme fichier. */
function clefDeFichier(fichier: File): string {
  return `${fichier.name}|${fichier.size}|${fichier.lastModified}|${fichier.type}`;
}

/** Les fichiers d'un depot, chacun une fois. */
export function fichiersUniques(fichiers: Iterable<File>): File[] {
  const vus = new Set<string>();
  return Array.from(fichiers).filter((fichier) => {
    const clef = clefDeFichier(fichier);
    if (vus.has(clef)) return false;
    vus.add(clef);
    return true;
  });
}

/** Ce depot repete-t-il le precedent, dans la seconde ? `precedent` est mis a jour sinon. */
export function memeGeste(precedent: { clef: string; quand: number }, fichiers: readonly File[], maintenant: number): boolean {
  const clef = fichiers.map(clefDeFichier).join('\n');
  if (clef === precedent.clef && maintenant - precedent.quand < DELAI_D_UN_GESTE_MS) return true;
  precedent.clef = clef;
  precedent.quand = maintenant;
  return false;
}

/** Le dernier depot, commun a toutes les copies du module (comme la marque). */
function dernierDepot(): { clef: string; quand: number } {
  const global = globalThis as { [DERNIER_DEPOT]?: { clef: string; quand: number } };
  global[DERNIER_DEPOT] ??= { clef: '', quand: 0 };
  return global[DERNIER_DEPOT];
}

/** Le glisser porte-t-il des fichiers venus de l'ordinateur ? */
function porteDesFichiers(event: DragEvent): boolean {
  return Array.from(event.dataTransfer?.types ?? []).includes('Files');
}

/**
 * ECOUTE LE GLISSER DE FICHIERS SUR TOUTE LA FENETRE, tant que `actif` : rend
 * `true` pendant qu'un fichier la survole, et passe a `deposer` les fichiers
 * laches, ou qu'ils tombent. Un compteur d'entrees et de sorties dit quand le
 * glisser quitte vraiment la fenetre (chaque element survole en envoie).
 *
 * UN GLISSER NE DANS LA PAGE NE COMPTE PAS (une image de la grille, une carte
 * de kanban) : Chrome dit « Files » meme pour une image de la page, et le voile
 * s'ouvrirait sous le doigt qui range une vignette.
 */
export function useGlisserDeFichiers(actif: boolean, deposer: (fichiers: File[]) => void): boolean {
  const [survol, setSurvol] = useState(false);
  const deposerRef = useRef(deposer);
  deposerRef.current = deposer;
  useEffect(() => {
    if (!actif) { setSurvol(false); return; }
    let profondeur = 0;
    let interne = false;
    const commence = (): void => { interne = true; };
    const finit = (): void => { interne = false; };
    const entre = (event: DragEvent): void => {
      if (interne || !porteDesFichiers(event)) return;
      profondeur += 1;
      setSurvol(true);
    };
    const survole = (event: DragEvent): void => {
      if (interne || !porteDesFichiers(event)) return;
      /* Sans ce refus du geste par defaut, le navigateur ouvrirait le fichier lache. */
      event.preventDefault();
      if (event.dataTransfer !== null) event.dataTransfer.dropEffect = 'copy';
    };
    const sort = (event: DragEvent): void => {
      if (interne || !porteDesFichiers(event)) return;
      profondeur = Math.max(0, profondeur - 1);
      if (profondeur === 0) setSurvol(false);
    };
    const depose = (event: DragEvent): void => {
      if (interne || !porteDesFichiers(event)) { interne = false; return; }
      event.preventDefault();
      profondeur = 0;
      setSurvol(false);
      const marque = event as DragEvent & { [MARQUE_DE_DEPOT]?: true };
      if (marque[MARQUE_DE_DEPOT] === true) return;
      marque[MARQUE_DE_DEPOT] = true;
      const fichiers = fichiersUniques(event.dataTransfer?.files ?? []);
      if (fichiers.length === 0 || memeGeste(dernierDepot(), fichiers, Date.now())) return;
      deposerRef.current(fichiers);
    };
    window.addEventListener('dragstart', commence);
    window.addEventListener('dragend', finit);
    window.addEventListener('dragenter', entre);
    window.addEventListener('dragover', survole);
    window.addEventListener('dragleave', sort);
    window.addEventListener('drop', depose);
    return () => {
      window.removeEventListener('dragstart', commence);
      window.removeEventListener('dragend', finit);
      window.removeEventListener('dragenter', entre);
      window.removeEventListener('dragover', survole);
      window.removeEventListener('dragleave', sort);
      window.removeEventListener('drop', depose);
      setSurvol(false);
    };
  }, [actif]);
  return survol;
}
